import { InvoiceLine, ServiceItem, ServiceTransaction } from '../types';
import type { UndatedSummary } from './invoiceLogic';
import { RAT_ADMIN, TorSperrAnzeige, anzeigeAlsText, leeresJournalRat } from './allocationGate';
import {
    TorMeldung,
    abbildbareLeistungen,
    kappeStack,
    meldeUeberHintergrundkanal,
    nichtAbbildbareZeilen,
    sicherMelden,
} from './allocationGateShadow';
import { zahl } from './allocationShadow';
import { formatCentsPlain } from './money';
import {
    ServiceJournalRow,
    isJournalMappable,
    journalToServiceTransactions,
    loadJournal,
    parseReportRef,
} from './serviceJournal';
import { buchungstag, isAccountingEntry, kennungFuerVergleich } from './serviceLogUtils';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER ENTWURF AUS DEM JOURNAL (S5a Stufe 3c, Paket E)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis Stufe 3b baute der Rechnungsentwurf (`buildProjectServiceLines`,
 * invoiceService) seine Mengen aus dem Projekt-JSON
 * (`metadata.globalData.serviceLog`). Am Tor verglichen 3a und 3b die Groessen
 * mit Geldwirkung, die dieser Aufbau aus dem Protokoll zieht — Zuordnung,
 * Zeitraum- und Stichtagsdeckel, Menge ohne Datum, Buchungsart — auf beiden
 * Protokollen: 138 Belege ohne Abweichung (Stufe 2/3a), 25/25 Rechnungen und
 * 3 Stornos still (3b), der Nachtlauf vom 02.11.2026 mit 11 284 Journalzeilen
 * ohne Wertabweichung. Controller-Entscheid 3c: der Entwurf schaltet um.
 *
 * Im JOURNAL-MODUS (Tor-Notschalter AUS) gilt jetzt:
 *   1. Das Protokoll des Entwurfs ist das Journal
 *      (`journalToServiceTransactions(loadJournal(projectId))`). Alles andere
 *      — LV, Reservierungen, Vorbelege, Zeitraum, Optionen — geht unveraendert
 *      in DIESELBE reine Kernrechnung (`baue`). Es gibt keine zweite Fassung
 *      der Regeln.
 *   2. Ein RUECKSCHATTEN rechnet dieselbe Kernrechnung ein zweites Mal ueber
 *      das JSON, mit denselben uebrigen Eingaben, und vergleicht
 *      (`vergleicheEntwurf`). Eine Abweichung geht ueber den Hintergrundkanal
 *      nach `bug_reports`, Klasse GELD oder ANZEIGE (unten). Sie sperrt nie,
 *      wirft nie und laesst den Entwurf nie warten: gerechnet wird synchron
 *      (Millisekunden, kein Netz), gemeldet ohne `await`. Der Entwurf behaelt
 *      die Fassung aus dem Journal. Synchron und nicht spaeter, weil die
 *      JSON-Seite ueber `isPostingEntry` das Buchungsart-Register liest — das
 *      ist global und veraenderlich; jetzt sieht sie es so, wie die
 *      JSON-Fassung bis 3b es sah.
 *   3. NICHT LESBAR (Entscheid 3, wie am Tor): `loadJournal` liefert null,
 *      oder das Journal ist leer, obwohl das JSON abbildbare Leistungen fuehrt
 *      (RLS — `abbildbareLeistungen`). Dann entsteht KEIN Entwurf, es gibt
 *      Klartext (`EntwurfJournalNichtLesbarError`) und eine Meldung. Kein
 *      stiller Rueckfall auf das JSON.
 *   4. NACHLAUF-RIEGEL (Nachtrag des Controllers): ein Entwurf darf nie mit
 *      kleinerer Menge entstehen, nur weil das Journal einer frisch gebuchten
 *      Leistung nachlaeuft — der Rueckschatten allein meldete das nur.
 *      Nachlauf liegt vor, wenn BEIDES gilt:
 *        (a) der Vergleich hat eine Abweichung der Klasse GELD, und
 *        (b) eine JSON-Zeile einer Leistung mit dieser GELD-Abweichung ist
 *            journal-abbildbar (`isJournalMappable`), ihre id steht aber im
 *            Journal nicht — verglichen ueber `kennungFuerVergleich` gegen
 *            `legacy_entry_id ?? id` ALLER Journalzeilen, auch der
 *            zurueckgenommenen (eine stillgelegte Zeile laeuft nicht nach,
 *            sie ist zurueckgenommen).
 *      KEIN Nachlauf: Eintraege nur im Journal (dann wurde das JSON
 *      ueberschrieben, und der Journal-Entwurf ist richtig), Wertunterschiede
 *      bei gleicher id, nicht abbildbare JSON-Zeilen, und fehlende Zeilen von
 *      Leistungen ohne GELD-Abweichung (z. B. am Beleg ausgeschlossen).
 *      Sind ALLE fehlenden Zeilen FRISCH (`bookedAt`, sonst `date`, hoechstens
 *      60 Minuten alt), wird das Journal in der Staffel 1,5 s / 3 s / 5 s neu
 *      geladen und neu gerechnet (zusammen 9,5 s Wartezeit). Reihen sie sich
 *      ein, entsteht der Entwurf normal — gezaehlt (`entwurfZaehler`), nicht
 *      gemeldet. Bleiben sie aus, scheitert ein Nachladen (dann bleibt der
 *      bekannte Befund stehen, kein Lesefehler) oder fehlt mindestens eine
 *      aeltere Zeile (Drift: Warten waere sinnlos, es wird NICHT gewartet,
 *      auch wenn daneben eine frische fehlt), entsteht KEIN Entwurf:
 *      `EntwurfNachlaufError` (Code JOURNAL_NACHLAUF) mit Klartext und eine
 *      Meldung eigener Art.
 *      Warum das nie dauerhaft sperrt, ausser bei echter Drift: eine frische
 *      Zeile kommt mit der Zweitschrift wenige Sekunden nach dem Speichern an
 *      (Korb mit Wiederholung, serviceJournal.ts); fehlt sie dauerhaft, ist es
 *      Drift — und die sperrte das Tor beim Ausstellen ohnehin
 *      (`eintrag_fehlt_im_journal` bzw. der Zeitraum- oder Stichtagsdeckel),
 *      bis die naechtliche Nachspiegelung sie nachtraegt.
 *
 * Im JSON-MODUS (Notschalter AN) laeuft nichts hiervon: der Aufbau rechnet wie
 * bis Stufe 3b aus dem JSON, ohne Journal-Aufruf, ohne Schatten, ohne Riegel.
 *
 * ─── Was verglichen wird ───────────────────────────────────────────────────
 * Die Zeilen-ids sind zufaellig; verglichen wird je `serviceId` (bei
 * doppelter Leistung je Vorkommen). Je Zeile `quantity`, `unitPriceCents`,
 * `netCents`, `flatCovered`, `previouslyBilledQty`, `previouslyInvoicedQty`,
 * `description`; am Ergebnis `loggedFrom`, `loggedTo`, `cumulativeFrom`,
 * `cumulativeBillingPeriod`, `outsidePeriodCount`, `performed`, `undated`,
 * `includeUndated`. Mengen mit EPS 0.0005 wie ueberall, Cent exakt.
 *   GELD     eine Zeile nur auf einer Seite, oder `quantity`, `netCents` bzw.
 *            `previouslyInvoicedQty` weichen ab.
 *   ANZEIGE  alles andere: Leistungszeitraum, Datumsvorschlaege,
 *            Monatstext der Pauschale, Abrechnungszeitraum-Vorschau,
 *            Undatiert-Zusammenfassung (und was am LV haengt und deshalb nie
 *            abweichen sollte: Preis, Pauschaldeckung, abgerechneter Stand).
 * Strukturelle Unterschiede werden NICHT unterdrueckt, sondern im Text
 * genannt wie in allocationGateShadow: je Leistung Zahl und Menge der
 * JSON-Zeilen, die nie ins Journal koennen, und der abbildbaren Leistungen,
 * die nur im JSON (Journal laeuft nach) bzw. nur im Journal (JSON
 * ueberschrieben) stehen.
 *
 * ─── Was nach bug_reports geht ─────────────────────────────────────────────
 * Titel „[S5a-Entwurf] <was> — Beleg <id|neu>, Projekt <nr> (<id>)", erste
 * Stackzeile „S5a Stufe 3c, Entwurf aus dem Journal — <KLASSE>: …" mit
 * KLASSE ∈ GELD, ANZEIGE, NICHT LESBAR, NACHLAUF, DRIFT, AUSGEFALLEN. Danach
 * Modus, Zeitraum, Beleg, die Zeile „Klassen: …", hoechstens 20 Punkte und
 * eine JSON-Zeile — dieselbe Lesart wie die Tor-Meldungen fuer die
 * Ueberwachung. Eine Meldung je Aufbau.
 *
 * DROSSEL: der Hintergrundkanal drosselt selbst (5 min je Fingerabdruck, 20
 * je Sitzung) — aber den Sitzungsdeckel teilen sich ALLE Hintergrundmeldungen.
 * Ein Rechnungslauf ueber 30 Projekte mit einer systematischen Abweichung
 * verbrauchte ihn, und die Sperrmeldungen des Tors gingen danach in dieser
 * Sitzung verloren. Der Entwurf hat deshalb ein eigenes, kleineres Kontingent
 * (`MAX_ENTWURF_MELDUNGEN` je Sitzung, 5 min je Meldung) davor.
 */

/** Wie in allocationLogic: darunter ist eine Menge Rundungsrauschen. */
const EPS = 0.0005;

/** Die Wartestaffel des Nachlauf-Riegels — zusammen 9,5 s. */
export const NACHLAUF_STAFFEL_MS: readonly number[] = [1_500, 3_000, 5_000];

/** Bis zu diesem Alter gilt eine fehlende Zeile als frisch gebucht. */
export const FRISCH_MS = 60 * 60_000;

/** Eine Zeitangabe bis so weit in der Zukunft zaehlt noch als jetzt (Uhrversatz). */
const UHRVERSATZ_MS = 5 * 60_000;

/** Hoechstens so viele fehlende Eintraege im Klartext; der Rest wird gezaehlt. */
const MAX_IM_TEXT = 3;

/** Hoechstens so viele Punkte je Meldung; der Rest wird gezaehlt. */
const MAX_PUNKTE = 20;

/** Eigenes Kontingent je Sitzung, vor dem gemeinsamen Deckel des Kanals (Kopf). */
export const MAX_ENTWURF_MELDUNGEN = 8;

// ═══════════════════════════════════════════════════════════════════════════
// Typen
// ═══════════════════════════════════════════════════════════════════════════

/** Was der Vergleich vom Ergebnis des Aufbaus liest — ProjectServiceLinesResult passt. */
export interface EntwurfErgebnis {
    lines: InvoiceLine[];
    loggedFrom?: string;
    loggedTo?: string;
    cumulativeFrom?: string;
    cumulativeBillingPeriod?: { from: string; to: string };
    outsidePeriodCount: number;
    performed: { from?: string; to?: string };
    undated: UndatedSummary;
    includeUndated: boolean;
}

/** Projekt, Beleg, Modus und Zeitraum eines Aufbaus — fuer Meldung und Klartext. */
export interface EntwurfKopf {
    projectId: string;
    projectNumber: string | null;
    /** Der Beleg, dessen Entwurf neu aufgebaut wird (`excludeInvoiceId`); null bei einer Neuanlage. */
    invoiceId: string | null;
    kumulativ: boolean;
    zeitraum: { from?: string; to?: string };
}

export type EntwurfKlasse = 'GELD' | 'ANZEIGE';

export type EntwurfFeld =
    | 'zeile_nur_im_journal'
    | 'zeile_nur_im_json'
    | 'quantity'
    | 'netCents'
    | 'previouslyInvoicedQty'
    | 'unitPriceCents'
    | 'flatCovered'
    | 'previouslyBilledQty'
    | 'description'
    | 'loggedFrom'
    | 'loggedTo'
    | 'cumulativeFrom'
    | 'cumulativeBillingPeriod'
    | 'outsidePeriodCount'
    | 'performed'
    | 'undated'
    | 'includeUndated';

/** Die Felder mit Geldwirkung (Kopf). */
const GELD_FELDER: ReadonlySet<EntwurfFeld> = new Set<EntwurfFeld>([
    'zeile_nur_im_journal',
    'zeile_nur_im_json',
    'quantity',
    'netCents',
    'previouslyInvoicedQty',
]);

export interface EntwurfDiff {
    klasse: EntwurfKlasse;
    feld: EntwurfFeld;
    /** Fehlt bei den Feldern am Ergebnis. */
    serviceId?: string;
    /** Wert aus dem Journal (massgeblich) bzw. aus dem JSON (Rueckschatten), lesbar. */
    journal: string;
    json: string;
    /** Ein Satz fuer einen Menschen. */
    text: string;
}

export interface EntwurfVergleich {
    gleich: boolean;
    /** GELD, sobald eine Abweichung Geld bewegt; null bei Gleichheit. */
    klasse: EntwurfKlasse | null;
    diffs: EntwurfDiff[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Der Vergleich — rein
// ═══════════════════════════════════════════════════════════════════════════

/** Eine Menge oder `undefined` — gleich nur, wenn beide fehlen oder auf EPS gleich sind. */
const mengeWeichtAb = (x: number | undefined, y: number | undefined): boolean => {
    if (x === undefined && y === undefined) return false;
    if (x === undefined || y === undefined) return true;
    // NaN zaehlt als Abweichung — eine krumme Menge zu verschweigen hiesse,
    // genau den Fall zu uebersehen, den ein Protokoll anders rechnet.
    return !(Math.abs(Number(x) - Number(y)) <= EPS);
};

const mengeText = (n: number | undefined): string => (n === undefined ? '—' : zahl(Number(n)));
const centText = (n: number | undefined): string =>
    n === undefined || !Number.isFinite(Number(n)) ? String(n ?? '—') : `${formatCentsPlain(Number(n))} EUR`;
const tagText = (s: string | undefined): string => s || '—';
const spanneText = (s: { from?: string; to?: string } | undefined): string =>
    s ? `${s.from || '…'} bis ${s.to || '…'}` : '—';

/** „Pos. 2.7.40 Baustellenbegehung (s1)" — aus der Belegzeile. */
const positionDerZeile = (l: InvoiceLine): string => {
    const pos = String(l.lvPosition ?? '').trim();
    const titel = String(l.title ?? '').trim();
    const name = [pos ? `Pos. ${pos}` : '', titel].filter(Boolean).join(' ');
    return `${name || 'Position'} (${l.serviceId ?? '?'})`;
};

/** Die Leistungszeilen je Leistung und Vorkommen — die ids sind zufaellig. */
const zeilenNachLeistung = (lines: InvoiceLine[] | null | undefined): Map<string, InvoiceLine> => {
    const out = new Map<string, InvoiceLine>();
    const vorkommen = new Map<string, number>();
    (lines ?? []).forEach((l, i) => {
        if (!l) return;
        const id = l.serviceId ?? `ohne-leistung-${i}`;
        const n = vorkommen.get(id) ?? 0;
        vorkommen.set(id, n + 1);
        out.set(n === 0 ? id : `${id}#${n + 1}`, l);
    });
    return out;
};

/**
 * Vergleicht den Entwurf aus dem Journal (massgeblich) mit dem aus dem JSON
 * (Rueckschatten). Rein: kein Netz, keine Uhr. Reihenfolge der Befunde: erst
 * GELD, dann ANZEIGE, je Klasse nach Leistung.
 */
export const vergleicheEntwurf = (journal: EntwurfErgebnis, json: EntwurfErgebnis): EntwurfVergleich => {
    const diffs: EntwurfDiff[] = [];
    const dazu = (feld: EntwurfFeld, serviceId: string | undefined, j: string, x: string, text: string) =>
        diffs.push({ klasse: GELD_FELDER.has(feld) ? 'GELD' : 'ANZEIGE', feld, serviceId, journal: j, json: x, text });

    // ── Je Zeile ──────────────────────────────────────────────────────────
    const links = zeilenNachLeistung(journal?.lines);
    const rechts = zeilenNachLeistung(json?.lines);
    for (const key of [...new Set([...links.keys(), ...rechts.keys()])].sort()) {
        const a = links.get(key);
        const b = rechts.get(key);
        if (!a || !b) {
            const l = (a ?? b) as InvoiceLine;
            const was = `Menge ${mengeText(l.quantity)}, ${centText(l.netCents)}`;
            if (a) {
                dazu('zeile_nur_im_journal', l.serviceId, was, '—', `${positionDerZeile(l)}: steht nur im Entwurf aus dem Journal (${was}), der JSON-Entwurf fuehrt sie nicht.`);
            } else {
                dazu('zeile_nur_im_json', l.serviceId, '—', was, `${positionDerZeile(l)}: steht nur im JSON-Entwurf (${was}), der Entwurf aus dem Journal fuehrt sie nicht.`);
            }
            continue;
        }
        const pos = positionDerZeile(a);
        const sid = a.serviceId;
        if (mengeWeichtAb(a.quantity ?? 0, b.quantity ?? 0)) {
            dazu('quantity', sid, mengeText(a.quantity), mengeText(b.quantity), `${pos}: Menge ${mengeText(a.quantity)} laut Journal gegen ${mengeText(b.quantity)} laut JSON.`);
        }
        if (Number(a.netCents ?? 0) !== Number(b.netCents ?? 0)) {
            dazu('netCents', sid, centText(a.netCents), centText(b.netCents), `${pos}: Betrag ${centText(a.netCents)} laut Journal gegen ${centText(b.netCents)} laut JSON.`);
        }
        if (mengeWeichtAb(a.previouslyInvoicedQty, b.previouslyInvoicedQty)) {
            dazu('previouslyInvoicedQty', sid, mengeText(a.previouslyInvoicedQty), mengeText(b.previouslyInvoicedQty), `${pos}: durch Belege gedeckt ${mengeText(a.previouslyInvoicedQty)} laut Journal gegen ${mengeText(b.previouslyInvoicedQty)} laut JSON (ALT-Deckung).`);
        }
        if (Number(a.unitPriceCents ?? 0) !== Number(b.unitPriceCents ?? 0)) {
            dazu('unitPriceCents', sid, centText(a.unitPriceCents), centText(b.unitPriceCents), `${pos}: Einzelpreis ${centText(a.unitPriceCents)} laut Journal gegen ${centText(b.unitPriceCents)} laut JSON.`);
        }
        if (Boolean(a.flatCovered) !== Boolean(b.flatCovered)) {
            dazu('flatCovered', sid, String(Boolean(a.flatCovered)), String(Boolean(b.flatCovered)), `${pos}: Pauschaldeckung ${a.flatCovered ? 'ja' : 'nein'} laut Journal gegen ${b.flatCovered ? 'ja' : 'nein'} laut JSON.`);
        }
        if (mengeWeichtAb(a.previouslyBilledQty, b.previouslyBilledQty)) {
            dazu('previouslyBilledQty', sid, mengeText(a.previouslyBilledQty), mengeText(b.previouslyBilledQty), `${pos}: abgerechneter Stand ${mengeText(a.previouslyBilledQty)} laut Journal gegen ${mengeText(b.previouslyBilledQty)} laut JSON.`);
        }
        if ((a.description ?? '') !== (b.description ?? '')) {
            dazu('description', sid, a.description ?? '', b.description ?? '', `${pos}: Monatstext „${(a.description ?? '').slice(0, 120)}" laut Journal gegen „${(b.description ?? '').slice(0, 120)}" laut JSON.`);
        }
    }

    // ── Am Ergebnis ───────────────────────────────────────────────────────
    const tag = (feld: EntwurfFeld, name: string, j: string | undefined, x: string | undefined) => {
        if ((j ?? '') !== (x ?? '')) dazu(feld, undefined, tagText(j), tagText(x), `${name} ${tagText(j)} laut Journal gegen ${tagText(x)} laut JSON.`);
    };
    tag('loggedFrom', 'Datumsvorschlag „Leistung von"', journal?.loggedFrom, json?.loggedFrom);
    tag('loggedTo', 'Datumsvorschlag „Leistung bis"', journal?.loggedTo, json?.loggedTo);
    tag('cumulativeFrom', 'Leistungsbeginn (kumulativ)', journal?.cumulativeFrom, json?.cumulativeFrom);
    const pa = journal?.cumulativeBillingPeriod;
    const pb = json?.cumulativeBillingPeriod;
    if ((pa?.from ?? '') !== (pb?.from ?? '') || (pa?.to ?? '') !== (pb?.to ?? '') || !pa !== !pb) {
        dazu('cumulativeBillingPeriod', undefined, spanneText(pa), spanneText(pb), `Abrechnungszeitraum-Vorschau ${spanneText(pa)} laut Journal gegen ${spanneText(pb)} laut JSON.`);
    }
    if (Number(journal?.outsidePeriodCount ?? 0) !== Number(json?.outsidePeriodCount ?? 0)) {
        dazu('outsidePeriodCount', undefined, String(journal?.outsidePeriodCount), String(json?.outsidePeriodCount), `Leistungen ausserhalb des Zeitraums: ${journal?.outsidePeriodCount} laut Journal gegen ${json?.outsidePeriodCount} laut JSON.`);
    }
    const sa = journal?.performed;
    const sb = json?.performed;
    if ((sa?.from ?? '') !== (sb?.from ?? '') || (sa?.to ?? '') !== (sb?.to ?? '')) {
        dazu('performed', undefined, spanneText(sa), spanneText(sb), `Leistungszeitraum ${spanneText(sa)} laut Journal gegen ${spanneText(sb)} laut JSON.`);
    }
    const ua = journal?.undated;
    const ub = json?.undated;
    if (Number(ua?.count ?? 0) !== Number(ub?.count ?? 0) || Number(ua?.netCents ?? 0) !== Number(ub?.netCents ?? 0)) {
        const u = (x: UndatedSummary | undefined) => `${x?.count ?? 0} Position(en), ${centText(x?.netCents ?? 0)}`;
        dazu('undated', undefined, u(ua), u(ub), `Ohne Datum offen: ${u(ua)} laut Journal gegen ${u(ub)} laut JSON.`);
    }
    if (Boolean(journal?.includeUndated) !== Boolean(json?.includeUndated)) {
        dazu('includeUndated', undefined, String(journal?.includeUndated), String(json?.includeUndated), 'Die Wahl „ohne Datum mitnehmen" weicht ab (interner Fehler: beide Laeufe bekamen dieselbe).');
    }

    const geordnet = [...diffs.filter((d) => d.klasse === 'GELD'), ...diffs.filter((d) => d.klasse === 'ANZEIGE')];
    return {
        gleich: geordnet.length === 0,
        klasse: geordnet.length === 0 ? null : geordnet[0].klasse,
        diffs: geordnet,
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Nachlauf-Riegel — rein
// ═══════════════════════════════════════════════════════════════════════════

/** Eine JSON-Zeile, die im Journal fehlt. */
export interface FehlenderEintrag {
    serviceId: string;
    entryId: string;
    date?: string;
    bookedAt?: string;
    quantity: number;
    reportNumber: number | null;
    /** Hoechstens 60 Minuten alt (`bookedAt`, sonst `date`). */
    frisch: boolean;
}

export interface NachlaufBefund {
    frisch: FehlenderEintrag[];
    alt: FehlenderEintrag[];
}

/** Gebucht vor hoechstens 60 Minuten? `bookedAt` zaehlt vor `date` (das ist oft der Tag der Arbeit). */
export const istFrisch = (e: Pick<ServiceTransaction, 'bookedAt' | 'date'>, jetzt: number): boolean => {
    const gebucht = Date.parse(String(e?.bookedAt ?? ''));
    const t = Number.isFinite(gebucht) ? gebucht : Date.parse(String(e?.date ?? ''));
    return Number.isFinite(t) && t >= jetzt - FRISCH_MS && t <= jetzt + UHRVERSATZ_MS;
};

/**
 * Liegt ein Nachlauf vor (Kopf, Punkt 4)? Null, wenn nicht. Rein bis auf
 * `jetzt`, das hereingereicht wird.
 */
export const nachlaufBefund = (
    vergleich: EntwurfVergleich,
    jsonLog: ServiceTransaction[],
    journal: ServiceJournalRow[],
    jetzt: number,
): NachlaufBefund | null => {
    if (vergleich.klasse !== 'GELD') return null;
    const geld = new Set(
        vergleich.diffs.filter((d) => d.klasse === 'GELD' && d.serviceId).map((d) => d.serviceId as string),
    );
    if (geld.size === 0) return null;
    // ALLE Journalzeilen, auch die zurueckgenommenen: eine stillgelegte
    // Zeile laeuft nicht nach (Kopf).
    const imJournal = new Set((journal ?? []).map((r) => kennungFuerVergleich(r?.legacy_entry_id ?? r?.id)));
    const frisch: FehlenderEintrag[] = [];
    const alt: FehlenderEintrag[] = [];
    for (const e of jsonLog ?? []) {
        if (!e?.id || !geld.has(e.serviceId) || !isJournalMappable(e)) continue;
        if (imJournal.has(kennungFuerVergleich(e.id))) continue;
        const f: FehlenderEintrag = {
            serviceId: e.serviceId,
            entryId: e.id,
            date: e.date,
            bookedAt: e.bookedAt,
            quantity: Number(e.quantity) || 0,
            reportNumber: parseReportRef(e).reportNumber,
            frisch: istFrisch(e, jetzt),
        };
        (f.frisch ? frisch : alt).push(f);
    }
    return frisch.length + alt.length > 0 ? { frisch, alt } : null;
};

/** „03.11.2026" aus dem Berliner Tag. */
const datum = (wert: string | undefined): string => {
    const tag = buchungstag(wert ?? null);
    if (!tag) return 'ohne Datum';
    const [j, m, t] = tag.split('-');
    return `${t}.${m}.${j}`;
};

/** „Pos. 2.7.40 Baustellenbegehung" — aus dem LV, sonst die Leistungs-id. */
const positionAusLv = (serviceId: string, leistungen: ServiceItem[]): string => {
    const s = (leistungen ?? []).find((x) => x?.id === serviceId);
    const name = [String(s?.lvPosition ?? '').trim(), String(s?.title ?? '').trim()].filter(Boolean).join(' ');
    return name ? `Pos. ${name}` : `Leistung ${serviceId}`;
};

const liste = (teile: string[], trenner: string): string =>
    teile.length > MAX_IM_TEXT
        ? `${teile.slice(0, MAX_IM_TEXT).join(trenner)} und ${teile.length - MAX_IM_TEXT} weitere`
        : teile.join(trenner);

/**
 * Der Klartext fuer den Nutzer — Wortlaut vom Controller (Nachtrag 3c).
 * Frische Zeilen: in einer Minute erneut erzeugen. Aeltere: Drift, ein
 * Administrator soll es wissen. Stehen beide an, beides.
 */
export const nachlaufText = (befund: NachlaufBefund, leistungen: ServiceItem[]): string => {
    const saetze: string[] = [];
    if (befund.frisch.length > 0) {
        const teile = befund.frisch.map(
            (f) =>
                `${positionAusLv(f.serviceId, leistungen)}, ${datum(f.date)}${f.reportNumber !== null ? `, Bericht Nr. ${f.reportNumber}` : ''}`,
        );
        saetze.push(
            befund.frisch.length === 1
                ? `Eine eben gebuchte Leistung ist noch nicht im Buchungsjournal angekommen (${teile[0]}). Bitte den Entwurf in einer Minute erneut erzeugen.`
                : `Eben gebuchte Leistungen sind noch nicht im Buchungsjournal angekommen (${liste(teile, '; ')}). Bitte den Entwurf in einer Minute erneut erzeugen.`,
        );
    }
    if (befund.alt.length > 0) {
        const teile = befund.alt.map(
            (f) =>
                `${positionAusLv(f.serviceId, leistungen)} vom ${datum(f.date)}${f.reportNumber !== null ? ` (Bericht Nr. ${f.reportNumber})` : ''}`,
        );
        saetze.push(
            befund.alt.length === 1
                ? `Projektprotokoll und Buchungsjournal weichen ab: ${teile[0]} fehlt im Buchungsjournal. Die nächtliche Nachspiegelung trägt ihn in der Regel nach; bitte einen Administrator informieren.`
                : `Projektprotokoll und Buchungsjournal weichen ab: ${liste(teile, ', ')} fehlen im Buchungsjournal. Die nächtliche Nachspiegelung trägt sie in der Regel nach; bitte einen Administrator informieren.`,
        );
    }
    return saetze.join(' ');
};

// ═══════════════════════════════════════════════════════════════════════════
// Die Fehler — Klartext fuer alle vier Aufrufer
// ═══════════════════════════════════════════════════════════════════════════
//
// Beide Fehler tragen ihren Klartext als `message`: der Editor (Neuaufbau)
// zeigt `message` mit Zeilenumbruechen, die Anlage aus der Liste
// (invoiceCreateFlow → describeCreateFailure) und der Rechnungslauf (je
// Gruppe in `failures`) ebenso; die Vorhersage des Abrechnungszeitraums im
// Editor ist eine Anzeige und laesst die Angabe bei jedem Fehler still weg.
// Bewusst NICHT der Code TOR_GESPERRT: der Ausstellen-Dialog zeigte den
// gescheiterten Aufbau sonst als Sperre am Tor.

export const ENTWURF_NICHT_LESBAR = 'ENTWURF_JOURNAL_NICHT_LESBAR' as const;
export const JOURNAL_NACHLAUF = 'JOURNAL_NACHLAUF' as const;

const projektName = (kopf: EntwurfKopf): string => kopf.projectNumber ?? kopf.projectId;

export const entwurfNichtLesbarAnzeige = (kopf: EntwurfKopf, leer: boolean): TorSperrAnzeige => ({
    // Ohne Gedankenstrich (Schreibregel des Betreibers vom 03.11.2026); der
    // Titel des Tors (allocationGate) stammt aus 3b und bleibt, wie er ist.
    titel: 'Buchungsjournal nicht lesbar. Bitte erneut versuchen.',
    punkte: [
        ...(leer
            ? [
                  leeresJournalRat(projektName(kopf), {
                      erneut: 'den Entwurf erneut erzeugen',
                      durchAdmin: 'den Entwurf von einem Administrator erzeugen lassen',
                  }),
              ]
            : []),
        `Projekt ${projektName(kopf)}: Die Positionen wurden nicht aus dem Leistungsstand aufgebaut, angelegt oder geändert wurde nichts.`,
    ],
    hinweis: RAT_ADMIN,
});

/** Das Journal ist nicht lesbar — kein Entwurf (Entscheid 3, kein Rueckfall auf das JSON). */
export class EntwurfJournalNichtLesbarError extends Error {
    readonly code: typeof ENTWURF_NICHT_LESBAR = ENTWURF_NICHT_LESBAR;

    constructor(
        readonly anzeige: TorSperrAnzeige,
        readonly projectId: string,
        /** true: leer zurueck, obwohl das JSON abbildbare Leistungen fuehrt (Leserecht). */
        readonly leer: boolean,
    ) {
        super(anzeigeAlsText(anzeige));
        this.name = 'EntwurfJournalNichtLesbarError';
    }
}

/** Das Journal laeuft nach (oder driftet) — kein Entwurf mit zu kleiner Menge. */
export class EntwurfNachlaufError extends Error {
    readonly code: typeof JOURNAL_NACHLAUF = JOURNAL_NACHLAUF;
    /**
     * 'nachlauf': es fehlen nur frische Zeilen; 'drift': mindestens eine
     * aeltere fehlt — dann hilft kein erneuter Versuch, auch wenn daneben
     * eine frische fehlt.
     */
    readonly art: 'nachlauf' | 'drift';

    constructor(
        readonly befund: NachlaufBefund,
        readonly projectId: string,
        text: string,
    ) {
        super(text);
        this.name = 'EntwurfNachlaufError';
        this.art = befund.alt.length > 0 ? 'drift' : 'nachlauf';
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// Die Meldungen
// ═══════════════════════════════════════════════════════════════════════════

const ERSTE_ZEILE = 'S5a Stufe 3c, Entwurf aus dem Journal —';

export const entwurfTitel = (was: string, kopf: EntwurfKopf): string => {
    const projekt = kopf.projectNumber ? `${kopf.projectNumber} (${kopf.projectId})` : kopf.projectId;
    return `[S5a-Entwurf] ${was} — Beleg ${kopf.invoiceId ?? 'neu'}, Projekt ${projekt}`;
};

const kopfZeile = (kopf: EntwurfKopf): string =>
    `Modus: ${kopf.kumulativ ? 'kumulativ' : 'fortschreibend'}; Zeitraum: ${kopf.zeitraum.from || '…'} bis ${kopf.zeitraum.to || '…'}; Beleg: ${kopf.invoiceId ?? 'neu (noch nicht angelegt)'}`;

const online = (): boolean | null => (typeof navigator !== 'undefined' ? navigator.onLine : null);

interface LeistungsHinweis {
    nichtAbbildbar?: { anzahl: number; menge: number };
    nurJson: { anzahl: number; menge: number };
    nurJournal: { anzahl: number; menge: number };
}

/**
 * Je Leistung, was strukturell zwischen den Protokollen liegt (Kopf,
 * „Strukturelle Unterschiede"): nie abbildbare JSON-Zeilen, und abbildbare
 * Leistungen (keine Buchungsvorgaenge), die nur auf einer Seite stehen.
 */
const protokollHinweise = (
    jsonLog: ServiceTransaction[],
    journalLog: ServiceTransaction[],
): Map<string, LeistungsHinweis> => {
    const out = new Map<string, LeistungsHinweis>();
    const von = (serviceId: string): LeistungsHinweis => {
        const h = out.get(serviceId) ?? { nurJson: { anzahl: 0, menge: 0 }, nurJournal: { anzahl: 0, menge: 0 } };
        out.set(serviceId, h);
        return h;
    };
    for (const [serviceId, n] of nichtAbbildbareZeilen(jsonLog)) von(serviceId).nichtAbbildbar = n;
    const leistung = (e: ServiceTransaction) => !!e?.id && !!e.serviceId && !isAccountingEntry(e) && isJournalMappable(e);
    const jsonIds = new Set(jsonLog.filter(leistung).map((e) => kennungFuerVergleich(e.id)));
    const journalIds = new Set(journalLog.filter(leistung).map((e) => kennungFuerVergleich(e.id)));
    for (const e of jsonLog.filter(leistung)) {
        if (journalIds.has(kennungFuerVergleich(e.id))) continue;
        const h = von(e.serviceId).nurJson;
        h.anzahl += 1;
        h.menge += Number(e.quantity) || 0;
    }
    for (const e of journalLog.filter(leistung)) {
        if (jsonIds.has(kennungFuerVergleich(e.id))) continue;
        const h = von(e.serviceId).nurJournal;
        h.anzahl += 1;
        h.menge += Number(e.quantity) || 0;
    }
    return out;
};

const hinweisText = (h: LeistungsHinweis | undefined): string => {
    if (!h) return '';
    const teile: string[] = [];
    if (h.nichtAbbildbar) {
        teile.push(`${h.nichtAbbildbar.anzahl} JSON-Zeile(n) dieser Leistung mit zusammen ${zahl(h.nichtAbbildbar.menge)} koennen nie ins Journal (ohne Datum oder lesbare Menge)`);
    }
    if (h.nurJson.anzahl > 0) {
        teile.push(`${h.nurJson.anzahl} Leistung(en) mit zusammen ${zahl(h.nurJson.menge)} nur im JSON (das Journal laeuft vermutlich nach)`);
    }
    if (h.nurJournal.anzahl > 0) {
        teile.push(`${h.nurJournal.anzahl} Leistung(en) mit zusammen ${zahl(h.nurJournal.menge)} nur im Journal (das JSON wurde vermutlich ueberschrieben)`);
    }
    return teile.length ? ` — ${teile.join('; ')}` : '';
};

const summe = (hinweise: Map<string, LeistungsHinweis>, welche: 'nurJson' | 'nurJournal'): number =>
    [...hinweise.values()].reduce((s, h) => s + h[welche].anzahl, 0);

const feldZeile = (diffs: EntwurfDiff[]): string => {
    const jeFeld = new Map<EntwurfFeld, number>();
    for (const d of diffs) jeFeld.set(d.feld, (jeFeld.get(d.feld) ?? 0) + 1);
    return [...jeFeld].map(([f, n]) => `${n}x ${f}`).join(', ');
};

const protokollZeile = (jsonLog: ServiceTransaction[], journalLog: ServiceTransaction[], hinweise: Map<string, LeistungsHinweis>): string => {
    const nicht = (jsonLog ?? []).filter((e) => !isJournalMappable(e)).length;
    const ohneLeistung = (jsonLog ?? []).filter((e) => !String(e?.serviceId ?? '').trim()).length;
    return `Protokolle: JSON ${jsonLog.length} Zeilen, davon ${nicht} nicht abbildbar (${ohneLeistung} ohne Leistung); Journal ${journalLog.length} lebende Zeilen; abbildbare Leistungen nur im JSON ${summe(hinweise, 'nurJson')}, nur im Journal ${summe(hinweise, 'nurJournal')}.`;
};

/** Die Abweichungsmeldung des Rueckschattens — GELD oder ANZEIGE. */
export const entwurfAbweichungsMeldung = (
    kopf: EntwurfKopf,
    vergleich: EntwurfVergleich,
    protokolle: { jsonLog: ServiceTransaction[]; journalLog: ServiceTransaction[] },
): TorMeldung => {
    const hinweise = protokollHinweise(protokolle.jsonLog ?? [], protokolle.journalLog ?? []);
    const geld = vergleich.diffs.filter((d) => d.klasse === 'GELD').length;
    const anzeige = vergleich.diffs.length - geld;
    const ersteZeile =
        vergleich.klasse === 'GELD'
            ? `${ERSTE_ZEILE} GELD: der Rueckschatten ueber das JSON-Protokoll weicht in Menge, Betrag oder Belegdeckung ab; OHNE Wirkung, der Entwurf traegt die Fassung aus dem Journal.`
            : `${ERSTE_ZEILE} ANZEIGE: der Rueckschatten ueber das JSON-Protokoll weicht nur in Anzeigen ab (Leistungszeitraum, Datumsvorschlag, Monatstext, Abrechnungszeitraum, ohne Datum); OHNE Wirkung, der Entwurf traegt die Fassung aus dem Journal.`;
    const zeilen = [
        ersteZeile,
        kopfZeile(kopf),
        `Klassen: ${geld} GELD, ${anzeige} ANZEIGE (${vergleich.diffs.length} Abweichungen; Felder: ${feldZeile(vergleich.diffs)})`,
        protokollZeile(protokolle.jsonLog ?? [], protokolle.journalLog ?? [], hinweise),
        ...vergleich.diffs
            .slice(0, MAX_PUNKTE)
            .map((d) => `- [${d.klasse} ${d.feld}] ${d.text}${d.serviceId ? hinweisText(hinweise.get(d.serviceId)) : ''}`),
        ...(vergleich.diffs.length > MAX_PUNKTE ? [`… und ${vergleich.diffs.length - MAX_PUNKTE} weitere`] : []),
        JSON.stringify({
            ...kopf,
            klasse: vergleich.klasse,
            geld,
            anzeige,
            anzahl: vergleich.diffs.length,
            nurJson: summe(hinweise, 'nurJson'),
            nurJournal: summe(hinweise, 'nurJournal'),
        }),
    ];
    return { message: entwurfTitel('Entwurf aus dem Journal weicht ab', kopf), stack: kappeStack(zeilen.join('\n')) };
};

/**
 * `leerTrotz` > 0: das Journal kam ohne Zeile, das JSON fuehrt so viele
 * abbildbare Leistungen (Leserecht). Die zweite Zeile ist wortgleich die des
 * Tors, damit die Ueberwachung beide gleich liest.
 */
export const entwurfNichtLesbarMeldung = (kopf: EntwurfKopf, leerTrotz = 0): TorMeldung => ({
    message: entwurfTitel('Entwurf: Buchungsjournal nicht lesbar', kopf),
    stack: kappeStack(
        [
            `${ERSTE_ZEILE} NICHT LESBAR: kein Entwurf erzeugt (Entscheid 3, kein Rueckfall auf das JSON).`,
            leerTrotz > 0
                ? `loadJournal lieferte KEINE Zeile, das Projektprotokoll fuehrt aber ${leerTrotz} abbildbare Leistungen — vermutlich fehlt das Leserecht (service_journal: is_admin() or can_access_project), verglichen wurde nichts.`
                : 'loadJournal lieferte null (Tabelle fehlt, kein Netz oder keine Berechtigung) — verglichen wurde nichts.',
            kopfZeile(kopf),
            JSON.stringify({ ...kopf, leerTrotz, online: online() }),
        ].join('\n'),
    ),
});

/** Der Rueckschatten selbst ist ausgefallen — OHNE Wirkung. */
export const entwurfAusfallMeldung = (kopf: EntwurfKopf, fehler: unknown): TorMeldung => {
    const e = (fehler ?? {}) as { message?: string; stack?: string };
    return {
        message: entwurfTitel('Rueckschatten im Entwurf ausgefallen', kopf),
        stack: kappeStack(
            [
                `${ERSTE_ZEILE} AUSGEFALLEN: der Rueckschatten ueber das JSON ist ausgefallen; OHNE Wirkung, der Entwurf traegt die Fassung aus dem Journal.`,
                `Fehler: ${String(e.message ?? fehler).slice(0, 300)}`,
                e.stack ?? '',
                kopfZeile(kopf),
                JSON.stringify(kopf),
            ].join('\n'),
        ),
    };
};

/**
 * Der Nachlauf-Riegel hat gehalten — NACHLAUF (nur frische Zeilen fehlen)
 * oder DRIFT (mindestens eine aeltere). `nachladenGescheitert`: ein erneutes
 * Laden des Journals lieferte nichts Lesbares, der Befund stammt vom letzten
 * gelungenen Laden — damit die Ueberwachung einen Netz- oder Rechteaussetzer
 * von echtem Nachlauf unterscheiden kann.
 */
export const entwurfNachlaufMeldung = (
    kopf: EntwurfKopf,
    befund: NachlaufBefund,
    vergleich: EntwurfVergleich | null,
    versuche: number,
    nachladenGescheitert = false,
): TorMeldung => {
    // Wie `EntwurfNachlaufError.art`: eine aeltere Zeile macht es zur Drift.
    const nachlauf = befund.alt.length === 0;
    const fehlend = [...befund.frisch, ...befund.alt];
    const diffs = vergleich?.diffs.filter((d) => d.klasse === 'GELD') ?? [];
    const punkte = [
        ...fehlend.map(
            (f) =>
                `- [fehlt_im_journal${f.frisch ? ' frisch' : ''}] Leistung ${f.serviceId}: Eintrag ${f.entryId} (Menge ${zahl(f.quantity)}, Tag ${buchungstag(f.date ?? null) ?? 'ohne'}, gebucht ${f.bookedAt ?? '—'}${f.reportNumber !== null ? `, Bericht Nr. ${f.reportNumber}` : ''}) steht im JSON, nicht im Journal.`,
        ),
        ...diffs.map((d) => `- [${d.klasse} ${d.feld}] ${d.text}`),
    ];
    return {
        message: entwurfTitel(
            nachlauf
                ? 'Entwurf nicht erzeugt: Journal laeuft nach'
                : 'Entwurf nicht erzeugt: Projektprotokoll und Journal weichen ab',
            kopf,
        ),
        stack: kappeStack(
            [
                nachlauf
                    ? nachladenGescheitert
                        ? `${ERSTE_ZEILE} NACHLAUF: kein Entwurf erzeugt; eine eben gebuchte Leistung fehlte im Buchungsjournal, und das erneute Laden scheiterte (Netz oder Rechte; Nachlauf-Riegel).`
                        : `${ERSTE_ZEILE} NACHLAUF: kein Entwurf erzeugt; eine eben gebuchte Leistung fehlte auch nach erneutem Laden im Buchungsjournal (Nachlauf-Riegel).`
                    : `${ERSTE_ZEILE} DRIFT: kein Entwurf erzeugt; im Buchungsjournal fehlt ein aelterer Eintrag des Projektprotokolls (Nachlauf-Riegel, ${versuche > 0 ? 'nach Warten' : 'ohne Warten'}).`,
                kopfZeile(kopf),
                `Fehlend: ${befund.frisch.length} frisch, ${befund.alt.length} aelter als 60 min; erneut geladen: ${versuche}x${nachladenGescheitert ? ', zuletzt gescheitert' : ''} (Staffel ${NACHLAUF_STAFFEL_MS.map((ms) => `${ms / 1000} s`).join(' / ')}); GELD-Abweichungen: ${diffs.length}`,
                ...punkte.slice(0, MAX_PUNKTE),
                ...(punkte.length > MAX_PUNKTE ? [`… und ${punkte.length - MAX_PUNKTE} weitere`] : []),
                JSON.stringify({
                    ...kopf,
                    art: nachlauf ? 'nachlauf' : 'drift',
                    frisch: befund.frisch.length,
                    alt: befund.alt.length,
                    versuche,
                    nachladenGescheitert,
                    online: online(),
                }),
            ].join('\n'),
        ),
    };
};

// ═══════════════════════════════════════════════════════════════════════════
// Das eigene Kontingent vor dem Hintergrundkanal
// ═══════════════════════════════════════════════════════════════════════════

export interface MeldeKontingent {
    /** Darf diese Meldung jetzt hinaus? Vermerkt sie, wenn ja. */
    darf: (m: TorMeldung, jetzt: number) => boolean;
    zuruecksetzen: () => void;
}

/**
 * Hoechstens `max` Meldungen je Sitzung, dieselbe (Titel + erste Stackzeile,
 * wie der Fingerabdruck des Kanals) hoechstens einmal je `sperrMs`. Ein
 * Wiederholungsaufbau desselben Entwurfs verbraucht so kein Kontingent.
 */
export const meldeKontingent = (max: number, sperrMs = 5 * 60_000): MeldeKontingent => {
    const zuletzt = new Map<string, number>();
    let gesendet = 0;
    return {
        darf: (m, jetzt) => {
            const k = `${m.message}\n${String(m.stack ?? '').split('\n', 1)[0]}`;
            const t = zuletzt.get(k);
            if (t !== undefined && jetzt - t < sperrMs) return false;
            if (gesendet >= max) return false;
            zuletzt.set(k, jetzt);
            gesendet += 1;
            return true;
        },
        zuruecksetzen: () => {
            zuletzt.clear();
            gesendet = 0;
        },
    };
};

const kontingent = meldeKontingent(MAX_ENTWURF_MELDUNGEN);

const zaehler = { nachlaufAufgeloest: 0, gedrosselt: 0 };

/**
 * Was nur gezaehlt und nicht gemeldet wird: ein Nachlauf, der sich beim
 * erneuten Laden aufgeloest hat, und Meldungen, die das eigene Kontingent
 * zurueckhielt. Ein Modul-Zaehler wie `serviceLogAccessStats`.
 */
export const entwurfZaehler = (): { nachlaufAufgeloest: number; gedrosselt: number } => ({ ...zaehler });

/** Nur fuer Tests: Kontingent und Zaehler zuruecksetzen. */
export const resetEntwurfMeldungen = (): void => {
    kontingent.zuruecksetzen();
    zaehler.nachlaufAufgeloest = 0;
    zaehler.gedrosselt = 0;
};

// ═══════════════════════════════════════════════════════════════════════════
// Der Aufbau im Journal-Modus
// ═══════════════════════════════════════════════════════════════════════════

export interface EntwurfDeps {
    ladeJournal: (projectId: string) => Promise<ServiceJournalRow[] | null>;
    melde: (m: TorMeldung) => void | Promise<void>;
    schlafe: (ms: number) => Promise<void>;
    jetzt: () => number;
}

export const STANDARD_ENTWURF_DEPS: EntwurfDeps = {
    // Erst beim Aufruf aufgeloest, nicht beim Laden des Moduls.
    ladeJournal: (projectId) => loadJournal(projectId),
    melde: (m) => meldeUeberHintergrundkanal(m),
    schlafe: (ms) => new Promise((ok) => setTimeout(ok, ms)),
    jetzt: () => Date.now(),
};

export interface EntwurfEingang<R extends EntwurfErgebnis> {
    kopf: EntwurfKopf;
    /** Das Protokoll aus dem JSON — fuer den Rueckschatten und den Riegel. */
    jsonLog: ServiceTransaction[];
    /** Die erste Ladung des Journals, parallel zum Projekt angefordert. */
    journal: ServiceJournalRow[] | null;
    /** Die reine Kernrechnung — dieselbe fuer beide Protokolle. */
    baue: (log: ServiceTransaction[]) => R;
    /** Das LV, fuer Position und Titel im Klartext. */
    leistungen: ServiceItem[];
}

/** Meldet ohne zu warten; baut die Meldung erst hier, und auch das darf nicht werfen. */
const meldeOhneWarten = (deps: EntwurfDeps, baue: () => TorMeldung): void => {
    try {
        const m = baue();
        if (!kontingent.darf(m, deps.jetzt())) {
            zaehler.gedrosselt += 1;
            return;
        }
        void sicherMelden(deps, m);
    } catch {
        // Die Meldung ist eine Zugabe: sie darf den Aufbau nie beruehren.
    }
};

/**
 * Der Aufbau im Journal-Modus (Kopf, Punkte 1–4): liefert die Fassung aus dem
 * Journal oder wirft `EntwurfJournalNichtLesbarError` bzw.
 * `EntwurfNachlaufError`. Ein Fehler der Kernrechnung auf dem JOURNAL geht
 * durch wie bis 3b; einer auf dem JSON (Rueckschatten) wird gemeldet und
 * geschluckt.
 */
export const entwurfAusDemJournal = async <R extends EntwurfErgebnis>(
    e: EntwurfEingang<R>,
    deps: EntwurfDeps = STANDARD_ENTWURF_DEPS,
): Promise<R> => {
    const { kopf } = e;
    const jsonLog = Array.isArray(e.jsonLog) ? e.jsonLog : [];
    // Wie am Tor: abbildbare LEISTUNGEN, keine Buchungsvorgaenge.
    const leistungen = abbildbareLeistungen(jsonLog);

    const lesbar = (rows: ServiceJournalRow[] | null | undefined): ServiceJournalRow[] => {
        if (!Array.isArray(rows)) {
            meldeOhneWarten(deps, () => entwurfNichtLesbarMeldung(kopf));
            throw new EntwurfJournalNichtLesbarError(entwurfNichtLesbarAnzeige(kopf, false), kopf.projectId, false);
        }
        if (rows.length === 0 && leistungen > 0) {
            meldeOhneWarten(deps, () => entwurfNichtLesbarMeldung(kopf, leistungen));
            throw new EntwurfJournalNichtLesbarError(entwurfNichtLesbarAnzeige(kopf, true), kopf.projectId, true);
        }
        return rows;
    };

    let rows = lesbar(e.journal);
    let journalLog = journalToServiceTransactions(rows);
    let ergebnis = e.baue(journalLog);

    // ── Rueckschatten: dieselbe Rechnung ueber das JSON, jetzt ──────────
    let ausfall: { fehler: unknown } | null = null;
    let jsonErgebnis: R | null = null;
    try {
        jsonErgebnis = e.baue(jsonLog);
    } catch (fehler) {
        ausfall = { fehler };
    }
    const vergleiche = (): EntwurfVergleich | null => {
        if (!jsonErgebnis) return null;
        try {
            return vergleicheEntwurf(ergebnis, jsonErgebnis);
        } catch (fehler) {
            ausfall = { fehler };
            return null;
        }
    };
    const befundJetzt = (v: EntwurfVergleich | null): NachlaufBefund | null => {
        if (!v) return null;
        try {
            return nachlaufBefund(v, jsonLog, rows, deps.jetzt());
        } catch (fehler) {
            // Ein kaputter Riegel haelt nicht an — das Tor bleibt das Netz.
            ausfall = { fehler };
            return null;
        }
    };
    let vergleich = vergleiche();
    let befund = befundJetzt(vergleich);

    // ── Nachlauf-Riegel ──────────────────────────────────────────────────
    // Gewartet wird nur, wenn ALLES Fehlende frisch ist. Fehlt daneben ein
    // aelterer Eintrag (Drift), bleibt der Entwurf ohnehin aus — dann sofort
    // melden statt bis zu 9,5 s zu warten (Codex-Review 3c, P3).
    let versuche = 0;
    let nachladenGescheitert = false;
    if (befund && befund.frisch.length > 0 && befund.alt.length === 0) {
        for (const ms of NACHLAUF_STAFFEL_MS) {
            await deps.schlafe(ms);
            versuche += 1;
            let neu: ServiceJournalRow[] | null = null;
            try {
                neu = await deps.ladeJournal(kopf.projectId);
            } catch {
                neu = null;
            }
            // Scheitert das Nachladen, bleibt der bekannte Befund stehen: der
            // Nutzer bekommt den Nachlauf-Text (erneut versuchen), und ein
            // Lesefehler verdeckt nicht den Grund, aus dem gewartet wurde.
            if (!Array.isArray(neu) || (neu.length === 0 && leistungen > 0)) {
                nachladenGescheitert = true;
                break;
            }
            rows = neu;
            journalLog = journalToServiceTransactions(rows);
            ergebnis = e.baue(journalLog);
            vergleich = vergleiche();
            befund = befundJetzt(vergleich);
            if (!befund) {
                zaehler.nachlaufAufgeloest += 1;
                break;
            }
            // Nur noch aeltere Zeilen oder eine dazugekommen: weiteres Warten
            // ist sinnlos.
            if (befund.frisch.length === 0 || befund.alt.length > 0) break;
        }
    }
    if (befund) {
        const b = befund;
        meldeOhneWarten(deps, () => entwurfNachlaufMeldung(kopf, b, vergleich, versuche, nachladenGescheitert));
        throw new EntwurfNachlaufError(b, kopf.projectId, nachlaufText(b, e.leistungen));
    }

    // ── Meldung des Rueckschattens, ohne zu warten ───────────────────────
    const ausgefallen = ausfall as { fehler: unknown } | null;
    if (ausgefallen) {
        meldeOhneWarten(deps, () => entwurfAusfallMeldung(kopf, ausgefallen.fehler));
    } else if (vergleich && !vergleich.gleich) {
        const v = vergleich;
        const log = journalLog;
        meldeOhneWarten(deps, () => entwurfAbweichungsMeldung(kopf, v, { jsonLog, journalLog: log }));
    }
    return ergebnis;
};
